import { createClient, type Session, type SupabaseClient } from "@supabase/supabase-js"

/**
 * Sign-in, as far as the rest of the client is concerned.
 *
 * The browser talks to Supabase directly to sign in and gets back a short-lived
 * access token. Every call to our own API then carries that token as
 * `Authorization: Bearer <token>`, and the server checks it (`api/auth.py`).
 *
 * The Supabase URL and publishable key come from `/api/auth/config` at start-up
 * rather than from build-time variables, so one built bundle works against any
 * project. Neither is a secret: the publishable key exists to be in browsers,
 * and the database's row-level security is what limits it.
 */

export interface AuthConfig {
  /** False only on a local machine with no Supabase project. */
  required: boolean
  supabaseUrl: string
  publishableKey: string
}

let client: SupabaseClient | null = null
const signedOutListeners = new Set<() => void>()

/**
 * Whether this page load came from a password-reset link. Read from the URL
 * before the Supabase client exists, because the client consumes the link's
 * fragment (`#...&type=recovery`) as it starts and announces the recovery
 * before anything else has had the chance to subscribe.
 */
let recoveryPending = false

/** True once, if this load was a password-reset link. */
export function takeRecovery(): boolean {
  const pending = recoveryPending
  recoveryPending = false
  return pending
}

export async function loadAuthConfig(): Promise<AuthConfig> {
  const response = await fetch("/api/auth/config")
  if (!response.ok) throw new Error(`sign-in configuration: HTTP ${response.status}`)
  return (await response.json()) as AuthConfig
}

/** Create the one Supabase client. Called once, by the gate, before anything renders. */
export function startAuth(config: AuthConfig): SupabaseClient | null {
  if (!config.required) return null
  if (client === null) {
    recoveryPending = new URLSearchParams(window.location.hash.slice(1)).get("type") === "recovery"
  }
  client ??= createClient(config.supabaseUrl, config.publishableKey, {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      // The confirmation e-mail links back here with the session in the URL.
      detectSessionInUrl: true,
    },
  })
  return client
}

export function authClient(): SupabaseClient | null {
  return client
}

/**
 * The current access token, refreshed first if it is about to expire, or null
 * when sign-in is off or nobody is signed in. Read per request rather than
 * cached here: the Supabase client owns the session and its refresh timer.
 */
export async function accessToken(): Promise<string | null> {
  if (!client) return null
  const { data } = await client.auth.getSession()
  return data.session?.access_token ?? null
}

export async function signOut(): Promise<void> {
  await client?.auth.signOut()
}

/** The server said 401: whatever session the page thought it had is not valid. */
export function reportUnauthenticated(): void {
  signedOutListeners.forEach((listener) => listener())
}

export function onUnauthenticated(listener: () => void): () => void {
  signedOutListeners.add(listener)
  return () => {
    signedOutListeners.delete(listener)
  }
}

export type { Session }
